// src/pages/SupportPage.tsx
//
// WS-D — Tenant-facing support page. Mirrors the GeniZone pattern
// (list/detail/create/reply via the `support.ts` Netlify function which
// scopes by tickets.customerid = tenant slug + app='genireports').

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, RefreshCw, AlertCircle, LifeBuoy, Plus,
  Loader2, MessageSquare, ChevronRight, Send,
} from 'lucide-react';
import { useAuthContext } from '../hooks/useAuth';
import { supportApi, type TicketResponse } from '../services/supportApi';
import Modal from '../components/Modal';
import { fmtLocalTime } from '../lib/formatTime';

interface SupportPageProps {
  onBack?: () => void;
}

type StatusFilter = 'active' | 'all' | 'open' | 'in-progress' | 'resolved' | 'closed';

export default function SupportPage({ onBack }: SupportPageProps) {
  const { branding } = useAuthContext();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const listQ = useQuery({
    queryKey: ['support', 'tickets', statusFilter],
    queryFn: () => supportApi.listTickets(statusFilter),
    refetchOnWindowFocus: false,
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-[1400px] mx-auto">
        <header className="border-b border-slate-800/50 backdrop-blur-sm bg-slate-950/50 sticky top-0 z-10">
          <div className="px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBack && (
                <button
                  onClick={onBack}
                  className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
                >
                  <ArrowLeft size={18} />
                </button>
              )}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-rose-500 to-rose-600 flex items-center justify-center text-white shadow-lg shadow-rose-500/20">
                  <LifeBuoy size={18} />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-white tracking-tight">Support</h1>
                  <p className="text-xs text-slate-500">
                    {branding?.company_name || 'GeniReports'} — open tickets &amp; history
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className="bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-1.5"
              >
                <option value="active">Active</option>
                <option value="all">All</option>
                <option value="open">Open</option>
                <option value="in-progress">In progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <button
                onClick={() => listQ.refetch()}
                disabled={listQ.isFetching}
                className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all disabled:opacity-50"
                title="Refresh"
              >
                <RefreshCw size={16} className={listQ.isFetching ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={() => setCreating(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-rose-500/20 border border-rose-500/40 text-rose-100 hover:bg-rose-500/30 transition-colors"
              >
                <Plus size={14} /> New ticket
              </button>
            </div>
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-8 py-6">
          {listQ.isLoading && (
            <Panel><div className="text-sm text-slate-500 flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Loading...</div></Panel>
          )}
          {listQ.error && (
            <Panel><ErrorBox err={listQ.error} /></Panel>
          )}
          {listQ.data?.notLinked && (
            <Panel>
              <div className="flex items-start gap-3 text-amber-200">
                <AlertCircle size={18} className="mt-0.5 shrink-0" />
                <div className="text-sm">
                  <div className="font-medium mb-1">Support not set up</div>
                  <div className="text-amber-200/80">
                    {listQ.data.message ?? 'This tenant is not yet linked to a Genovate billing customer. Your admin needs to set the support_client_id on the tenant.'}
                  </div>
                </div>
              </div>
            </Panel>
          )}
          {listQ.data && !listQ.data.notLinked && listQ.data.tickets.length === 0 && !listQ.isLoading && (
            <Panel><div className="text-sm text-slate-500 py-6 text-center">No tickets {statusFilter !== 'all' ? `(${statusFilter})` : ''} — click <em>New ticket</em> to open one.</div></Panel>
          )}
          {listQ.data && listQ.data.tickets.length > 0 && (
            <Panel>
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-700/40">
                  <tr>
                    <th className="text-left py-2 font-medium">#</th>
                    <th className="text-left py-2 font-medium">Subject</th>
                    <th className="text-left py-2 font-medium">Status</th>
                    <th className="text-left py-2 font-medium">Priority</th>
                    <th className="text-left py-2 font-medium">Source</th>
                    <th className="text-left py-2 font-medium">Updated</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {listQ.data.tickets.map((t) => (
                    <tr
                      key={t.id}
                      onClick={() => setOpenId(t.id)}
                      className="border-b border-slate-700/20 cursor-pointer hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="py-2 font-mono text-xs text-slate-400">{t.ticket_number}</td>
                      <td className="py-2 text-slate-200">{t.subject}</td>
                      <td className="py-2"><StatusBadge status={t.status} /></td>
                      <td className="py-2"><PriorityBadge priority={t.priority} /></td>
                      <td className="py-2"><AppChip app={(t as unknown as { app?: string }).app} /></td>
                      <td className="py-2 text-xs text-slate-500">{fmtLocalTime(t.updated_at)}</td>
                      <td className="py-2 text-right text-slate-600"><ChevronRight size={14} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          )}
        </main>

        {openId && (
          <TicketDetailModal ticketId={openId} onClose={() => setOpenId(null)} />
        )}
        {creating && (
          <CreateTicketModal
            onClose={() => setCreating(false)}
            onCreated={(id) => { setCreating(false); setOpenId(id); }}
          />
        )}
      </div>
    </div>
  );
}

// ── Subcomponents ─────────────────────────────────────────────────

function Panel({ children }: { children: React.ReactNode }) {
  return <div className="bg-slate-900/40 border border-slate-800/50 rounded-2xl p-5">{children}</div>;
}

function ErrorBox({ err }: { err: unknown }) {
  return (
    <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm p-4 flex items-start gap-2">
      <AlertCircle size={16} className="mt-0.5 shrink-0" />
      <div>{err instanceof Error ? err.message : String(err)}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const s = (status || '').toLowerCase();
  const colors =
    s === 'open'         ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' :
    s === 'in-progress' || s === 'in_progress' ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' :
    s === 'resolved'     ? 'bg-sky-500/15 text-sky-300 border-sky-500/30' :
    s === 'closed'       ? 'bg-slate-600/30 text-slate-300 border-slate-600/40' :
                           'bg-slate-700/40 text-slate-300 border-slate-600/40';
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${colors}`}>{s.toUpperCase()}</span>;
}

function AppChip({ app }: { app?: string }) {
  if (!app) return <span className="text-[10px] text-slate-600 italic">—</span>;
  const colors: Record<string, string> = {
    genireports: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    geniforms:   'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    genovate:    'bg-violet-500/15 text-violet-300 border-violet-500/30',
    genizone:    'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30',
  };
  const cls = colors[app.toLowerCase()] ?? 'bg-slate-700/40 text-slate-300 border-slate-600/40';
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${cls}`}>{app}</span>;
}

function PriorityBadge({ priority }: { priority: string }) {
  const p = (priority || '').toLowerCase();
  const colors =
    p === 'urgent' ? 'bg-red-500/20 text-red-200 border-red-500/40' :
    p === 'high'   ? 'bg-orange-500/15 text-orange-300 border-orange-500/30' :
    p === 'medium' ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' :
                     'bg-slate-700/40 text-slate-300 border-slate-600/40';
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${colors}`}>{p.toUpperCase()}</span>;
}

// ── Detail modal ──────────────────────────────────────────────────

function TicketDetailModal({ ticketId, onClose }: { ticketId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const detailKey = ['support', 'tickets', ticketId];

  const detailQ = useQuery({
    queryKey: detailKey,
    queryFn: () => supportApi.getTicket(ticketId),
    refetchOnWindowFocus: false,
  });

  const [reply, setReply] = useState('');

  const replyMut = useMutation({
    mutationFn: () => supportApi.addResponse(ticketId, reply.trim()),
    onSuccess: () => {
      setReply('');
      qc.invalidateQueries({ queryKey: detailKey });
      qc.invalidateQueries({ queryKey: ['support', 'tickets'] });
    },
  });

  const ticket = detailQ.data?.ticket;
  const responses = detailQ.data?.responses ?? [];

  return (
    <Modal
      onClose={onClose}
      title={ticket ? `${ticket.ticket_number} — ${ticket.subject}` : 'Ticket'}
      panelClassName="max-w-3xl"
    >
      {detailQ.isLoading && (
        <div className="text-sm text-slate-500 flex items-center gap-2 py-6">
          <RefreshCw size={14} className="animate-spin" /> Loading...
        </div>
      )}
      {detailQ.error && <ErrorBox err={detailQ.error} />}

      {ticket && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <StatusBadge status={ticket.status} />
            <PriorityBadge priority={ticket.priority} />
            <span className="text-slate-600">·</span>
            <span className="text-slate-400">{ticket.category}</span>
            <span className="text-slate-600">·</span>
            <span className="text-slate-500">opened {fmtLocalTime(ticket.created_at)} by {ticket.customer_email}</span>
          </div>

          <div className="bg-slate-800/40 border border-slate-700/40 rounded-lg p-4">
            <div className="text-xs text-slate-500 mb-1">Original request</div>
            <div className="text-sm text-slate-200 whitespace-pre-wrap">{ticket.description}</div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
              <MessageSquare size={11} /> Thread ({responses.length})
            </div>
            {responses.length === 0 ? (
              <div className="text-xs text-slate-500 italic py-2">No replies yet.</div>
            ) : (
              <ul className="space-y-3">
                {responses.map((r) => <ResponseRow key={r.id} response={r} />)}
              </ul>
            )}
          </div>

          {ticket.status !== 'closed' && (
            <form
              onSubmit={(e) => { e.preventDefault(); if (reply.trim() && !replyMut.isPending) replyMut.mutate(); }}
              className="space-y-2 pt-3 border-t border-slate-700/40"
            >
              <label className="block text-xs uppercase tracking-wider text-slate-500">Reply</label>
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={3}
                placeholder="Type your reply…"
                className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2"
              />
              {replyMut.error && <ErrorBox err={replyMut.error} />}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={!reply.trim() || replyMut.isPending}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-rose-500/20 border border-rose-500/40 text-rose-100 hover:bg-rose-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {replyMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Send
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </Modal>
  );
}

function ResponseRow({ response }: { response: TicketResponse }) {
  const isSupport = response.author_type === 'support';
  return (
    <li className={`rounded-lg p-3 border ${
      isSupport
        ? 'bg-violet-500/5 border-violet-500/20'
        : 'bg-slate-800/40 border-slate-700/40'
    }`}>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <div className="flex items-center gap-2">
          <span className={`font-medium ${isSupport ? 'text-violet-300' : 'text-slate-200'}`}>
            {response.author}
          </span>
          {isSupport && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/30">SUPPORT</span>
          )}
        </div>
        <span className="text-slate-600">{fmtLocalTime(response.timestamp)}</span>
      </div>
      <div className="text-sm text-slate-200 whitespace-pre-wrap">{response.message}</div>
    </li>
  );
}

// ── Create modal ──────────────────────────────────────────────────

function CreateTicketModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [category, setCategory] = useState('general');

  const mut = useMutation({
    mutationFn: () => supportApi.createTicket({
      subject: subject.trim(),
      description: description.trim(),
      priority,
      category,
    }),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['support', 'tickets'] });
      onCreated(t.id);
    },
  });

  const canSave = subject.trim().length > 0 && description.trim().length > 0 && !mut.isPending;

  return (
    <Modal onClose={onClose} title="New ticket" panelClassName="max-w-xl">
      <form
        onSubmit={(e) => { e.preventDefault(); if (canSave) mut.mutate(); }}
        className="space-y-4"
      >
        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            placeholder="One-line summary"
            className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2"
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
            required
            placeholder="What's happening? Include steps to reproduce if it's a bug."
            className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Priority</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-md px-3 py-2"
            >
              <option value="general">General</option>
              <option value="bug">Bug</option>
              <option value="feature_request">Feature request</option>
              <option value="billing">Billing</option>
              <option value="data">Data / report</option>
            </select>
          </div>
        </div>

        {mut.error && <ErrorBox err={mut.error} />}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-700/40">
          <button
            type="button"
            onClick={onClose}
            disabled={mut.isPending}
            className="px-4 py-2 text-sm text-slate-300 hover:text-white rounded-md hover:bg-slate-800/60 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-rose-500/20 border border-rose-500/40 text-rose-100 hover:bg-rose-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {mut.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
            Open ticket
          </button>
        </div>
      </form>
    </Modal>
  );
}
